import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { sbomApi } from './sbomApi';
import { formatStampShort } from '../utils/datetime';
import { SEV_COLOR, relLabel } from './SbomDashboard';
import '../styles/DocReview.css';
import '../styles/Risk.css';
import '../styles/TraceDashboard.css';
import './SbomDashboard.css';

/**
 * SBOM AI Studio (v4.32.0, mockup board 4) — Risk Studio's structure for a release's vulnerabilities:
 * sources the AI may read, six tasks, results a person accepts. The AI only proposes: *Apply* saves
 * each ticked draft through the Vulnerabilities page's own assessment route (same rules, same log).
 */

const POLL_MS = 1500;
const TASK_DESC = {
  vex: 'Proposes Affected / Not affected / Under investigation per finding, with the justification and the evidence it used.',
  rank: 'Orders findings by reachability on this device — interfaces, network exposure, KEV — not by CVSS alone.',
  upgrade: 'The version bumps that clear the most points, one per component; flags major-version jumps.',
  link_risk: 'For each Affected finding with no risk: the risk in your Risk panel it creates, or the risk the file lacks.',
  check: 'Components that cannot be checked, missing licences or versions, duplicate packages, stale or unassessed KEV verdicts.',
  ask: 'A free question over the sources; the answer cites the findings and keys it rests on.',
};
const VERDICT = {
  affected: ['Affected', { background: '#FBE0D5', color: '#8A2A0B' }],
  not_affected: ['Not affected', { background: '#E3ECF8', color: '#234B85' }],
  investigating: ['Under investigation', { background: '#FFF1D6', color: '#7A4B00' }],
};
const REACH = {
  high: { background: '#FBE0D5', color: '#8A2A0B' }, medium: { background: '#FFF1D6', color: '#7A4B00' },
  low: { background: '#E3ECF8', color: '#234B85' }, unknown: { background: '#F3F0F8', color: '#4A4560' },
};
const LEVEL = { bad: ['Act now', { background: '#7A1F05', color: '#fff' }], warn: ['Fix', { background: '#FFF1D6', color: '#7A4B00' }], info: ['Note', { background: '#F3F0F8', color: '#4A4560' }] };
const DECISION = { applied: 'Saved', rejected: 'Rejected', no_match: 'No match' };
const words = (s) => (s || '').replace(/_/g, ' ');

function Sev({ sev, kev }) {
  return (
    <span className="rsk-nowrap rsk-sub">
      <i className="sbd-dot" style={{ background: SEV_COLOR[sev] || SEV_COLOR.Unknown }} />{sev}
      {kev && <span className="sbd-kev">KEV</span>}
    </span>
  );
}

function Finding({ r }) {
  return (
    <>
      <strong>{r.component} {r.version}</strong>
      <div className="rsk-sub">{r.vuln_id} · <Sev sev={r.severity} kev={r.kev} /></div>
    </>
  );
}

export default function SbomStudio({ onAuth }) {
  const [params, setParams] = useSearchParams();
  const [home, setHome] = useState(null);
  const [error, setError] = useState('');
  const [task, setTask] = useState(params.get('task') || 'vex');
  const [scope, setScope] = useState('unassessed');
  const [prompt, setPrompt] = useState('');
  const [chosen, setChosen] = useState(null);
  const [run, setRun] = useState(null);           // {run, step, result}
  const [ticked, setTicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const release = params.get('release') || '';

  const load = useCallback(async (rel) => {
    try {
      const d = await sbomApi.studio(rel);
      setHome(d);
      setError('');
      setChosen((cur) => cur || new Set(d.sources.filter((x) => x.available && (!x.file || x.enabled)).map((x) => x.id)));
      return d;
    } catch (e) {
      if (!onAuth(e)) setError(e.message);
      return null;
    }
  }, [onAuth]);
  useEffect(() => { load(release); }, [load, release]);

  const openRun = useCallback(async (id) => {
    if (!id) { setRun(null); return; }
    try {
      const d = await sbomApi.studioGetRun(id);
      setRun(d);
      setTicked(new Set((d.result?.rows || []).filter((r) => r.decision === 'proposed').map((r) => r.id)));
    } catch (e) { if (!onAuth(e)) setError(e.message); }
  }, [onAuth]);

  // The newest run of the chosen task opens by itself.
  const rid = home?.release || '';
  useEffect(() => {
    if (!home) return;
    const last = (home.runs || []).find((r) => r.task === task);
    if (!run || run.run.release_item_id !== rid || run.run.task !== task) openRun(last?.id);
  }, [home, task, rid]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Poll a running run.
  const running = run?.run?.status === 'running';
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(async () => {
      try {
        const d = await sbomApi.studioGetRun(run.run.id);
        setRun(d);
        if (d.run.status !== 'running') {
          setTicked(new Set((d.result?.rows || []).filter((r) => r.decision === 'proposed').map((r) => r.id)));
          load(rid);
        }
      } catch { /* keep polling */ }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [running, run?.run?.id, load, rid]);   // eslint-disable-line react-hooks/exhaustive-deps

  const pickRelease = (v) => {
    const next = new URLSearchParams(params);
    next.set('release', v);
    setParams(next, { replace: true });
    setRun(null);
  };
  const pickTask = (t) => {
    setTask(t);
    if (t === 'link_risk') setScope('affected_no_risk');
    else if (scope === 'affected_no_risk') setScope('unassessed');
    const next = new URLSearchParams(params);
    next.set('task', t);
    setParams(next, { replace: true });
  };
  const toggleSource = (id) => setChosen((cur) => {
    const n = new Set(cur || []);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const start = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await sbomApi.studioRun({ release_item_id: rid, task, scope, prompt, sources: [...(chosen || [])] });
      await openRun(r.run_id);
      load(rid);
    } catch (e) { if (!onAuth(e)) setError(e.message); } finally { setBusy(false); }
  };
  const cancel = async () => {
    try { await sbomApi.studioCancel(run.run.id); openRun(run.run.id); } catch (e) { if (!onAuth(e)) setError(e.message); }
  };
  const decide = async (apply) => {
    setBusy(true);
    try {
      const ids = [...ticked];
      const r = apply ? await sbomApi.studioApply(run.run.id, ids) : await sbomApi.studioReject(run.run.id, ids);
      setRun((cur) => ({ ...cur, result: r.result }));
      setTicked(new Set());
      if (apply && r.failed) setError(`${r.failed} could not be saved — the reason is on each row.`);
    } catch (e) { if (!onAuth(e)) setError(e.message); } finally { setBusy(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const f = await sbomApi.studioUpload(file);
      setChosen((cur) => new Set([...(cur || []), f.id]));
      await load(rid);
    } catch (e) { if (!onAuth(e)) setError(e.message); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const removeFile = async (id) => {
    try { await sbomApi.studioDeleteFile(id.replace(/^file:/, '')); load(rid); } catch (e) { if (!onAuth(e)) setError(e.message); }
  };

  const taskInfo = useMemo(() => Object.fromEntries((home?.tasks || []).map((t) => [t.id, t])), [home]);
  const res = run?.result;
  const rows = res?.rows || [];
  const canDecide = run && run.run.status === 'done' && (run.run.task === 'vex' || run.run.task === 'link_risk');
  const tickable = (r) => r.decision === 'proposed';
  const rel = (home?.releases || []).find((r) => r.item_id === rid);
  const pastRuns = (home?.runs || []).filter((r) => r.task === task);

  return (
    <div className="drv-page rsk-page sbd-page">
      <div className="rsk-crumbs">
        <Link to="/bom/sw-dashboard">Software BOM · Dashboard</Link><span>/</span>
        {rel && <><Link to={`/bom/sw-dashboard/${rid}`}>{relLabel(rel)}</Link><span>/</span></>}
        <strong>SBOM AI Studio</strong>
      </div>
      <div className="drv-head">
        <div className="sbd-head-left">
          <h1 className="drv-title">SBOM AI Studio</h1>
          {home?.releases?.length > 0 && (
            <label className="sbd-rel">
              <span>Release</span>
              <select className="drv-select" value={rid} onChange={(e) => pickRelease(e.target.value)}>
                {home.releases.map((r) => <option key={r.item_id} value={r.item_id}>{relLabel(r)}</option>)}
              </select>
            </label>
          )}
        </div>
        {rid && <Link className="drv-btn" to={`/bom/sw-dashboard/${rid}`}>← Back to the dashboard</Link>}
      </div>
      {error && <div className="drv-error" role="alert">{error}</div>}
      {!home && !error && <div className="drv-muted">Loading…</div>}
      {home && !rid && <div className="rsk-card tdb-empty"><p>No releases yet — import an SBOM first.</p></div>}

      {home && rid && (
        <>
          <div className="sbd-studio-top">
            <section className="rsk-card rsk-pad" aria-label="Sources" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="rsk-row-between">
                <div className="rsk-lbl">Sources — what the AI may read</div>
                <button type="button" className="drv-link rsk-small-link" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? 'Reading…' : '+ Add document'}
                </button>
                <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.txt,.md" onChange={(e) => upload(e.target.files?.[0])} />
              </div>
              {home.sources.map((x) => (
                <label key={x.id} className={`sbd-src${x.available ? '' : ' sbd-src--off'}`}>
                  <input type="checkbox" disabled={!x.available} checked={!!chosen?.has(x.id) && x.available} onChange={() => toggleSource(x.id)} />
                  <span><strong>{x.name}</strong><span>{x.detail}</span></span>
                  {x.file && (
                    <button type="button" className="sbd-src-x" aria-label={`Remove ${x.name}`} title="Remove this document"
                      onClick={(e) => { e.preventDefault(); removeFile(x.id); }}>×</button>
                  )}
                </label>
              ))}
              <div className="rsk-fine">Sources are fenced as data. The AI never writes anything — every result goes through a person.</div>
            </section>

            <section className="rsk-card rsk-pad" aria-label="Tasks" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="rsk-lbl">Tasks</div>
              <div className="sbd-tasks">
                {(home.tasks || []).map((t) => {
                  const off = t.ai && !home.ai;
                  return (
                    <button key={t.id} type="button" className={`sbd-task${task === t.id ? ' sbd-task--on' : ''}`}
                      onClick={() => pickTask(t.id)} aria-pressed={task === t.id} disabled={off}
                      title={off ? 'AI is not enabled for this account' : undefined}>
                      <span className="sbd-task-top"><strong>{t.name}</strong><span className={`sbd-tag${t.ai ? '' : ' sbd-tag--no'}`}>{t.ai ? 'AI' : 'No AI'}</span></span>
                      <span className="sbd-task-desc">{TASK_DESC[t.id]}</span>
                    </button>
                  );
                })}
              </div>
              <div className="sbd-runbar">
                {task === 'ask' ? (
                  <textarea className="drv-input rsk-textarea" rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g. Which findings touch the network stack? Is anything reachable over Bluetooth?" maxLength={2000} />
                ) : !['check', 'link_risk'].includes(task) && (
                  <label className="drv-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <span className="drv-lbl">Scope</span>
                    <select className="drv-select" value={scope} onChange={(e) => setScope(e.target.value)}>
                      {(home.scopes || []).filter((x) => x.id !== 'affected_no_risk').map((x) => <option key={x.id} value={x.id}>{x.name} ({x.n})</option>)}
                    </select>
                  </label>
                )}
                {task === 'link_risk' && <span className="rsk-fine">Every Affected finding with no risk linked ({(home.scopes || []).find((x) => x.id === 'affected_no_risk')?.n || 0}).</span>}
                {running
                  ? <button type="button" className="drv-btn" onClick={cancel}>Cancel</button>
                  : <button type="button" className="drv-btn drv-btn--primary" onClick={start} disabled={busy || (taskInfo[task]?.ai && !home.ai)}>Run — {taskInfo[task]?.name}</button>}
                {pastRuns.length > 0 && (
                  <label className="drv-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                    <span className="drv-lbl">Run</span>
                    <select className="drv-select" value={run?.run?.id || ''} onChange={(e) => openRun(e.target.value)}>
                      {pastRuns.map((r) => <option key={r.id} value={r.id}>{formatStampShort(r.created_at)} · {r.created_by_name} · {r.status}</option>)}
                    </select>
                  </label>
                )}
              </div>
              {!home.ai && <div className="rsk-fine">AI is not enabled for this account — Plan upgrades and Check my SBOM still work.</div>}
            </section>
          </div>

          {run && (
            <section className="rsk-card" aria-label="Results">
              <div className="rsk-card-head">
                <div style={{ minWidth: 0 }}>
                  <h2 className="rsk-card-title">{taskInfo[run.run.task]?.name}{res?.summary ? ` — ${res.summary}` : ''}</h2>
                  <div className="rsk-card-meta">
                    {formatStampShort(run.run.created_at)} · {run.run.created_by_name}
                    {res?.sources_used?.length ? ` · ${res.sources_used.map((x) => x.name).join(', ')}` : ''}
                  </div>
                </div>
                {canDecide && (
                  <div className="rsk-actions">
                    <button type="button" className="drv-btn" onClick={() => decide(false)} disabled={busy || !ticked.size}>Reject ticked</button>
                    <button type="button" className="drv-btn drv-btn--primary" onClick={() => decide(true)} disabled={busy || !ticked.size}>
                      {run.run.task === 'vex' ? `Save ${ticked.size} as assessments` : `Link ${ticked.size} risk${ticked.size === 1 ? '' : 's'}`}
                    </button>
                  </div>
                )}
              </div>
              {running && (
                <div className="tdb-progress" role="status">
                  <div className="rsk-trk"><i style={{ width: '40%' }} /></div>
                  <span>{run.step || 'Working…'}</span>
                </div>
              )}
              {run.run.status === 'error' && <div className="drv-error" role="alert" style={{ margin: '12px 18px' }}>{run.run.error}</div>}
              {run.run.status === 'cancelled' && <div className="drv-muted" style={{ padding: '12px 18px' }}>Cancelled.</div>}
              {(res?.warnings || []).length > 0 && (
                <div className="rsk-fine" style={{ padding: '8px 18px' }}>{res.warnings.join(' · ')}</div>
              )}
              {run.run.status === 'done' && <Results task={run.run.task} res={res} rows={rows} ticked={ticked} setTicked={setTicked} tickable={tickable} />}
              {run.run.status === 'done' && canDecide && (
                <div className="rsk-fine" style={{ padding: '10px 18px' }}>
                  Saving goes through the Vulnerabilities page&apos;s own rules and history — each one is recorded as drafted by
                  SBOM AI Studio and confirmed by you. Nothing is written to Orcanos from here.
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Results({ task, res, rows, ticked, setTicked, tickable }) {
  const tick = (id) => setTicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const all = rows.filter(tickable);
  const allOn = all.length > 0 && all.every((r) => ticked.has(r.id));
  const head = (cols) => (
    <thead><tr>
      {(task === 'vex' || task === 'link_risk') && (
        <th style={{ width: 28 }}>
          <input type="checkbox" aria-label="Select all" checked={allOn} disabled={!all.length}
            onChange={() => setTicked(allOn ? new Set() : new Set(all.map((r) => r.id)))} />
        </th>
      )}
      {cols.map((c) => <th key={c}>{c}</th>)}
    </tr></thead>
  );
  const rowCls = (r) => (r.decision === 'applied' ? 'sbd-row--done' : r.decision === 'rejected' ? 'sbd-row--rej' : '');
  const box = (r) => (
    <td>{tickable(r) ? <input type="checkbox" checked={ticked.has(r.id)} onChange={() => tick(r.id)} aria-label={`Accept ${r.vuln_id}`} />
      : <span className="rsk-fine">{DECISION[r.decision] || ''}</span>}</td>
  );

  if (task === 'ask') {
    return (
      <>
        <div className="sbd-answer">{res?.answer}</div>
        {res?.cited?.length > 0 && <div className="rsk-pad rsk-look-keys">{res.cited.map((k) => <span key={k}>{k}</span>)}</div>}
      </>
    );
  }
  if (!rows.length) return <div className="drv-muted" style={{ padding: 18 }}>Nothing to show.</div>;
  return (
    <div className="rsk-table-wrap">
      <table className="rsk-table" style={{ minWidth: 1000 }}>
        {task === 'vex' && (
          <>
            {head(['Component · finding', 'Proposed verdict', 'Why — with evidence', 'Confidence'])}
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={rowCls(r)}>
                  {box(r)}
                  <td><Finding r={r} /></td>
                  <td>
                    <span className="rsk-pill" style={VERDICT[r.verdict]?.[1]}>{VERDICT[r.verdict]?.[0]}</span>
                    <div className="rsk-fine">{words(r.justification || r.response)}{r.risk_key ? ` · ${r.risk_key}` : ''}</div>
                  </td>
                  <td className="sbd-why">
                    {r.reason}
                    {r.evidence?.length > 0 && <div className="sbd-ev">{r.evidence.join(' · ')}</div>}
                    {r.error && <div className="sbd-err">{r.error}</div>}
                  </td>
                  <td>
                    <div className="sbd-conf" role="img" aria-label={`confidence ${r.confidence} percent`}><b style={{ width: `${r.confidence}%` }} /></div>
                    <div className="rsk-fine">{r.confidence >= 80 ? 'High' : r.confidence >= 60 ? 'Medium' : 'Low'}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </>
        )}
        {task === 'link_risk' && (
          <>
            {head(['Component · finding', 'Proposed risk', 'Why'])}
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={rowCls(r)}>
                  {box(r)}
                  <td><Finding r={r} /></td>
                  <td>
                    {r.risk_key ? <><span className="rsk-key">{r.risk_key}</span> <span className="rsk-fine">fit {r.fit}</span><div className="rsk-name">{r.risk_name}</div></>
                      : <><span className="rsk-pill" style={{ background: '#FFF1D6', color: '#7A4B00' }}>No matching risk</span>
                        {r.new_risk && <div className="rsk-fine">The file lacks: {r.new_risk}</div>}</>}
                  </td>
                  <td className="sbd-why">{r.reason}{r.error && <div className="sbd-err">{r.error}</div>}</td>
                </tr>
              ))}
            </tbody>
          </>
        )}
        {task === 'rank' && (
          <>
            {head(['#', 'Component · finding', 'Reachability', 'Why', 'Verdict'])}
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.rank}</strong></td>
                  <td><Finding r={r} /></td>
                  <td><span className="rsk-pill" style={REACH[r.reachability]}>{r.reachability}</span></td>
                  <td className="sbd-why">{r.reason}</td>
                  <td className="rsk-sub">{r.label}</td>
                </tr>
              ))}
            </tbody>
          </>
        )}
        {task === 'upgrade' && (
          <>
            {head(['Component', 'Upgrade', 'Clears', 'Score gain'])}
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.component}</strong></td>
                  <td>{r.version} → <strong>{r.target}</strong>{r.major && <span className="rsk-pill" style={{ marginLeft: 8, background: '#FFF1D6', color: '#7A4B00' }}>major version</span>}</td>
                  <td>{r.clears} finding{r.clears === 1 ? '' : 's'}<div className="rsk-fine">{r.severities}</div></td>
                  <td><strong>+{r.points}</strong></td>
                </tr>
              ))}
            </tbody>
          </>
        )}
        {task === 'check' && (
          <>
            {head(['', 'Component', 'Issue'])}
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><span className="rsk-pill" style={LEVEL[r.level][1]}>{LEVEL[r.level][0]}</span></td>
                  <td><strong>{r.component}</strong></td>
                  <td className="sbd-why">{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </>
        )}
      </table>
    </div>
  );
}
